import { PaymentMethod, PaymentMethodLabels, PaymentMethods } from '@alliance-disposal/transport-types';

export type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

export const FIVE_MIN_MS = 5 * 60 * 1000;

/**
 * Wayste-only payment methods (excludes Sourgum payment methods: REVENUE_SHARE, WIRE, ACCOUNT_CREDIT)
 * Use these constants throughout the Wayste app to ensure only appropriate payment methods are shown
 */
export const WaystePaymentMethods = PaymentMethods.filter(
    (method) => method === 'ACH' || method === 'CHECK' || method === 'CASH' || method === 'CREDIT_CARD',
);

export const WaystePaymentMethodLabels: Partial<Record<PaymentMethod, string>> = {
    ACH: PaymentMethodLabels.ACH,
    CASH: PaymentMethodLabels.CASH,
    CHECK: PaymentMethodLabels.CHECK,
    CREDIT_CARD: PaymentMethodLabels.CREDIT_CARD,
};

export const channelTypes = {
    facebookMarketplace: 'Facebook Marketplace',
    facebookPPC: 'Facebook Ad',
    internet: 'Internet Search',
    adwords: 'Google AdWords',
    bingAds: 'Bing Ads',
    referral: 'Referral',
    salesRep: 'Sales rep',
    yelp: 'Yelp',
    hometownLocal: 'Hometown Local',
    other: 'Other - Slack Joe & Put in Notes',
    unknown: 'Unknown',
};

export const channelTypesEnums = {
    facebookMarketplace: 'facebookMarketplace',
    facebookPPC: 'facebookPPC',
    internet: 'internet',
    adwords: 'adwords',
    bingAds: 'bingAds',
    referral: 'referral',
    salesRep: 'salesRep',
    yelp: 'yelp',
    hometownLocal: 'hometownLocal',
    other: 'other',
    unknown: 'unknown',
};

export const durationUnitTypes = {
    DAYS: 'Days',
    HOURS: 'Hours',
    WEEKS: 'Weeks',
};

export const durationUnitEnums = {
    DAYS: 'DAYS',
    HOURS: 'HOURS',
    WEEKS: 'WEEKS',
} as const;

export const customerTypes = {
    homeowner: 'Homeowner',
    contractor: 'Contractor',
    realtor: 'Realtor',
    business: 'Business',
    broker: 'Broker',
    hauler: 'Another hauler',
    unknown: 'Unknown',
};

export const customerTypesEnums = {
    homeowner: 'homeowner',
    contractor: 'contractor',
    realtor: 'realtor',
    business: 'business',
    broker: 'broker',
    hauler: 'hauler',
    unknown: 'unknown',
};

export const customerMotivations = {
    moving: 'Moving',
    sellingHome: 'Selling Home',
    forTenant: 'For Tenant',
    death: 'Death',
    decluttering: 'De-Cluttering',
    renovation: 'Renovation',
    storeClosing: 'Store Closing',
    storeOpening: 'Store Opening',
    warehouseCleanout: 'Warehouse Clean-out',
    other: 'Other - Slack Joe & Put in Notes',
};

export const customerMotivationsEnums = {
    moving: 'moving',
    sellingHome: 'sellingHome',
    forTenant: 'forTenant',
    death: 'death',
    decluttering: 'decluttering',
    renovation: 'renovation',
    storeClosing: 'storeClosing',
    storeOpening: 'storeOpening',
    warehouseCleanout: 'warehouseCleanout',
    other: 'other',
};

export const departmentTypes = {
    primary: 'Primary',
    site: 'Site contact',
    billing: 'Billing',
    other: 'Other (specify in notes)',
};

export const aggregateMaterialKeys = ['concrete', 'comix', 'concreteRebar', 'asphalt', 'dirt'];

export enum OrderStatus {
    UNASSIGNED = 'UNASSIGNED',
    ON_HOLD = 'ON_HOLD',
    ASSIGNED = 'ASSIGNED',
    DELIVERED = 'DELIVERED',
    READY_FULL_PICKUP = 'READY_FULL_PICKUP',
    READY_EMPTY_PICKUP = 'READY_EMPTY_PICKUP',
    PICKED_UP_FULL = 'PICKED_UP_FULL',
    PICKED_UP_EMPTY = 'PICKED_UP_EMPTY',
    DUMPED = 'DUMPED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
}

export const OrderStatusText = {
    UNASSIGNED: 'open',
    ON_HOLD: 'on hold',
    ASSIGNED: 'assigned',
    DELIVERED: 'delivered',
    READY_FULL_PICKUP: 'ready pick up',
    DUMPED: 'dumped',
    CANCELLED: 'canceled',
    COMPLETED: 'completed',
};

export const orderImageTypes = {
    dumpTicket: 'Dump Ticket',
    haulerPayable: 'Invoice from Hauler',
    shareWithAll: 'Share with Hauler & Customer',
    shareWithHauler: 'Share with Hauler Only',
    shareWithCustomer: 'Share with Customer Only',
    other: 'Other',
};

export const whenToCharge = {
    now: 'Charge Now',
    later: 'Charge Later',
    saveCard: 'Charge Later & Save Card',
} as const;
export const whenToChargeEnums = {
    now: 'now',
    saveCard: 'saveCard',
    later: 'later',
} as const;

export const priceTypes = {
    ton: 'Ton',
    yard: 'Yard',
    flat: 'Flat Rate',
};

/**
 * Price Type options
 * @enum {String}
 */
export const priceTypesEnums = {
    ton: 'ton',
    yard: 'yard',
    flat: 'flat',
};

export const leadTypes = {
    rolloff: 'Roll Off',
    commercial: 'Commercial',
    residential: 'Residential',
};

export const leadTypesEnums = {
    rolloff: 'rolloff',
    commercial: 'commercial',
    residential: 'residential',
};

export const leadStatusTypes = {
    open: 'Open',
    won: 'Won',
    lost: 'Lost',
};

export const leadStatusTypesEnums = {
    open: 'open',
    won: 'won',
    lost: 'lost',
};

export const leadReasonsLost = {
    noResponse: 'No Response',
    noAvailability: 'We Had No Availability',
    price: 'We were too expensive',
    diffHauler: 'Went With a Different Hauler',
    noLongerNeedService: 'They No Longer Need Any Service At All',
    outOfArea: 'Out of Service Area',
    unknown: 'Unknown',
    other: 'Other',
};

export const leadReasonsLostEnums = {
    noResponse: 'noResponse',
    noAvailability: 'noAvailability',
    price: 'price',
    diffHauler: 'diffHauler',
    noLongerNeedService: 'noLongerNeedService',
    outOfArea: 'outOfArea',
    unknown: 'unknown',
    other: 'other',
};

export const mediumTypes = {
    phone: 'Phone',
    email: 'Email',
    text: 'Text',
};

export const mediumTypesEnums = {
    phone: 'phone',
    email: 'email',
    text: 'Text',
};

export const rentalUnits = {
    day: 'day',
    week: 'week',
};

export const rollOffSizes = {
    10: '10',
    15: '15',
    20: '20',
    30: '30',
    40: '40',
    100: '100',
};

export const commercialSizes = {
    64: '64 Gallon',
    96: '96 Gallon',
    1: '1 Yard',
    2: '2 Yard',
    3: '3 Yard',
    4: '4 Yard',
    6: '6 Yard',
    8: '8 Yard',
};

export const residentialSizes = {
    64: '64',
    96: '96',
};

export const commercialMaterials = {
    trash: 'Trash',
    ssr: 'SSR',
    cardboard: 'Cardboard',
    food: 'Food Waste',
    foodOil: 'Organic Oil Waste',
    other: 'Other',
};

export const commercialMaterialsEnums = {
    trash: 'trash',
    ssr: 'ssr',
    cardboard: 'cardboard',
    food: 'food',
    foodOil: 'foodOil',
    other: 'other',
};

export const residentialMaterials = {
    trash: 'Trash',
    ssr: 'SSR',
    cardboard: 'Cardboard',
    other: 'Other',
};

export const residentialMaterialsEnums = {
    trash: 'trash',
    ssr: 'ssr',
    cardboard: 'cardboard',
    other: 'other',
};

export const commercialBillingFrequencies = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
};

export const commercialBillingFrequenciesEnums = {
    monthly: 'monthly',
    quarterly: 'quarterly',
};

export const residentialBillingFrequencies = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
};

export const residentialBillingFrequenciesEnums = {
    monthly: 'monthly',
    quarterly: 'quarterly',
};

export const commercialServiceFrequency = {
    week1: '1 x Week',
    week2: '2 x Week',
    week3: '3 x Week',
    week4: '4 x Week',
    week5: '5 x Week',
    eow: 'Every Other Week',
    month1: '1 x Month',
};

export const commercialServiceFrequencyEnums = {
    week1: 'week1',
    week2: 'week2',
    week3: 'week3',
    week4: 'week4',
    week5: 'week5',
    eow: 'eow',
    month1: 'month1',
};

export const residentialServiceFrequency = {
    week1: '1 x Week',
    week2: '2 x Week',
    week3: '3 x Week',
    week4: '4 x Week',
    week5: '5 x Week',
    eow: 'Every Other Week',
};

export const residentialServiceFrequencyEnums = {
    week1: 'week1',
    week2: 'week2',
    week3: 'week3',
    week4: 'week4',
    week5: 'week5',
    eow: 'eow',
};

export const commercialLocationTypes = {
    apartments: 'Apartments/Lodging',
    auto: 'Automotive',
    church: 'Church',
    college: 'College/University',
    entertainment: 'Entertainment Venue',
    grocer: 'Grocery Store',
    gym: 'Gym',
    hotel: 'Hotel',
    k12: 'K-12 School',
    manufacturer: 'Manufacturing/Industrial',
    office: 'Office',
    restaurant: 'Restaurant/Bar',
    retail: 'Retail',
    salon: 'Salon/Barber',
    nonProfit: 'Non-Profit',
    other: 'Other - Slack Joe & Put in Notes',
};

export const commercialLocationTypesEnums = {
    apartments: 'apartments',
    auto: 'auto',
    church: 'church',
    college: 'college',
    entertainment: 'entertainment',
    grocer: 'grocer',
    gym: 'gym',
    hotel: 'hotel',
    k12: 'k12',
    manufacturer: 'manufacturer',
    office: 'office',
    restaurant: 'restaurant',
    retail: 'retail',
    salon: 'salon',
    nonProfit: 'nonProfit',
    other: 'other',
};

export const quotedPriceItems = {
    haul: 'QP-Haul',
    dump: 'QP-Dump',
    adj: 'QP-Adj',
    overage: 'Overage',
};

export const haulerChargeItems: { [key: string]: string } = {
    haul: 'Haul',
    dump: 'Dump',
    tripCharge: 'Trip Charge',
    contamination: 'Contamination Fee',
    rentalExtension: 'Rental Extension',
    overfill: 'Overfill Fee',
    liveLoad: 'Live Load',
    surcharge: 'Surcharge',
    extraFeeItem: 'Additional Fee Item(s)',
    dumpsterLiner: 'Dumpster Liner',
    frozenLoad: 'Frozen Load',
    emergency: 'Emergency Work',
    labor: 'Labor',
    portaPotty: 'Porta Potty',
    refund: 'Refund',
};

export const zoneTypes = {
    county: 'Entire County',
    kml: 'KML File',
};

export const zoneTypesEnums = {
    county: 'county',
    kml: 'kml',
};

export const permitTypes = {
    permit: 'Permit Required',
    policePermission: 'Police permission needed only',
    notAllowed: 'Dumpsters are not allowed to be placed here',
    none: 'No permit or permission needed',
};

export const refundReason = {
    duplicate: 'Duplicate',
    requested_by_customer: 'Requested by Customer',
    other: 'Other',
};
export const refundReasonEnums: { [x in keyof typeof refundReason]: string } = {
    duplicate: 'duplicate',
    requested_by_customer: 'requested_by_customer',
    other: 'other',
};
export const needsAttentionStrings = ['Marked as Needs Attention', 'No longer Needs Attention'];

export const officeTel = '+17323669355';
export const mainSite = 'www.alliancedsp.com';
export const supportEmail = 'support@alliancedsp.com';
export const dispatchEmail = 'dispatch@alliancedsp.com';
export const billingEmail = 'billing@alliancedsp.com';
export const salesEmail = 'sales@alliancedsp.com';
export const billingAddress = '30 Montgomery St, Suite 1330, Jersey City, NJ 07302';
export const allianceHaulerID = '061fa815-cb3e-4738-82fc-9cc0eb11a8b2';
export const emailOptions = [salesEmail, dispatchEmail, supportEmail, billingEmail];
